export const CONTACT_EMAIL = "contact@thrive-businessdevelopment.com";
export const CALENDLY_URL = "https://calendly.com/balbirniebd/30min";

export const FOUNDER = {
  name: "Alistair Reid",
  caption: "Alistair Reid, Founder, Thrive Business Development.",
  alt: "Alistair Reid, founder of Thrive Business Development.",
  photo: "/alistair-reid.jpg",
  eyebrow: "About the Founder",
  headline: "Senior commercial experience, focused on practical growth",
  intro: [
    "I’m Alistair Reid, founder of Thrive Business Development.",
    "My background spans business development, commercial relationship management and talent solutions, with a strong focus on energy, engineering and technical markets. Throughout my career, I have worked with businesses to understand their needs, develop trusted relationships and identify opportunities for growth.",
    "I have seen capable businesses with strong services and experienced teams struggle to reach the right clients. Often, the challenge is knowing where to focus: which markets offer the strongest fit, which companies have a credible reason to buy, and how to start a meaningful commercial conversation.",
    "I created Thrive to help businesses answer those questions and act with greater confidence.",
  ],
  why: {
    title: "Why I created Thrive",
    body: [
      "Thrive combines structured market research with practical business-development experience.",
      "We help clients understand where their capabilities are most relevant, identify potential customers and prioritise their commercial efforts. Each recommendation should give the client a clear reason to act.",
    ],
    lead: "I work directly with leadership and business-development teams to:",
    items: [
      "Clarify the commercial value of their services",
      "Define their ideal customer profile",
      "Identify relevant markets and potential clients",
      "Prioritise commercial opportunities",
      "Understand the people involved in purchasing decisions",
      "Build a practical approach to developing new business",
    ],
  },
  sections: [
    {
      title: "Backed by senior commercial expertise",
      body: [
        "Thrive is founder-led and supported by a team of Commercial Growth Partners with senior backgrounds in business development, commercial leadership and management.",
        "We bring relevant experience into each engagement according to the client’s market, needs and growth objectives. This gives businesses access to senior commercial expertise through a focused, responsive consultancy.",
        "As founder, I remain directly involved in understanding our clients, shaping the work and helping turn the findings into practical next steps.",
      ],
    },
    {
      title: "Supported by better intelligence",
      body: [
        "Our approach is supported by commercial-intelligence technology being developed to connect supplier capabilities with relevant markets, potential clients and decision-makers.",
        "Technology helps broaden and organise our research. Senior commercial judgement guides how we assess the findings and translate them into useful recommendations.",
        "Clients receive clear priorities, supporting evidence and a practical route forward.",
      ],
    },
    {
      title: "A direct and practical approach",
      body: [
        "I believe good business development starts with understanding the business, recognising where it can create value and building relationships around that value.",
        "That principle runs through Thrive: clear thinking, focused research and practical support that helps clients move forward.",
        "If you would like to explore where your strongest commercial opportunities may be, I would welcome a conversation.",
      ],
    },
  ],
  cta: "Book a confidential growth conversation",
};

export const GROWTH_SERVICES = [
  { id: "commercial-intelligence", title: "Commercial Intelligence", body: "Market research, account intelligence, competitor insight, opportunity identification and commercial signals.", outcome: "Better commercial decisions based on clearer market intelligence." },
  { id: "global-market-mapping", title: "Global Market Mapping", body: "Identify markets, sectors, companies, projects, geographies and growth opportunities.", outcome: "A clearer view of where commercial effort should be focused." },
  { id: "opportunity-account-mapping", title: "Opportunity & Account Mapping", body: "Identify and prioritise the companies most relevant to the client's proposition.", outcome: "A focused target-account strategy instead of an unfocused prospect list." },
  { id: "decision-maker-intelligence", title: "Decision-Maker Intelligence", body: "Identify relevant commercial, operational, procurement and technical decision-makers.", outcome: "A clearer route into priority accounts." },
  { id: "market-entry-expansion", title: "Market Entry & Expansion", body: "Support businesses exploring new countries, regions and sectors.", outcome: "A more informed, lower-risk route into a new market." },
  { id: "fractional-bd", title: "Fractional Business Development", body: "Experienced senior business-development support without the requirement for an immediate permanent hire.", outcome: "Additional senior commercial capability exactly where it is required." },
  { id: "pipeline-development", title: "Pipeline Development", body: "Support target-account engagement, outreach, relationship development and opportunity progression.", outcome: "Move from intelligence to commercial conversations." },
  { id: "commercial-growth-support", title: "Commercial Growth Support", body: "Flexible senior BD support for specific growth initiatives.", outcome: "Practical help turning commercial strategy into activity." },
];

export const START_OPTIONS = [
  ["new-customers", "Find new customers"],
  ["new-market", "Explore a new market"],
  ["new-region", "Enter a new country or region"],
  ["map-companies", "Map target companies"],
  ["decision-makers", "Find decision-makers"],
  ["pipeline", "Strengthen our BD pipeline"],
  ["senior-bd", "Access senior BD support"],
  ["other", "Something else"],
];

export const SUGGESTED_PROMPTS = [
  "Find potential customers for my business",
  "I want to enter the Middle East",
  "Help me understand a new market",
  "Who should we be targeting?",
  "I need additional BD support",
  "Help me build my pipeline",
];

export const OFFICES = [
  {
    id: "aberdeen",
    city: "Aberdeen",
    lines: ["Neospace", "Riverside Drive", "Aberdeen", "AB11 7LH"],
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Neospace%2C+Riverside+Drive%2C+Aberdeen+AB11+7LH",
  },
  {
    id: "darlington",
    city: "Darlington",
    lines: ["Business Central", "Innovation Central", "10 John Williams Boulevard South", "Central Park", "Darlington", "DL1 1BF"],
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Business+Central%2C+10+John+Williams+Boulevard+South%2C+Central+Park%2C+Darlington+DL1+1BF",
  },
];

export const SERVICES = [
  {
    id: "market-opportunity-intelligence",
    num: "01",
    title: "Market Opportunity Intelligence",
    short:
      "Understand where the strongest commercial opportunities are and which parts of the market deserve your attention.",
    challenge:
      "Growth becomes harder when market visibility is limited. Familiar accounts are often well understood, but the broader commercial landscape, including new sectors, emerging demand and overlooked opportunities, can be much less clear.",
    does: "We build a clearer picture of the market around your business, helping you understand where commercial potential exists, where demand is developing and which areas are most relevant to your growth ambitions.",
    receive: [
      "A clearer view of your commercial landscape",
      "Priority areas of opportunity",
      "Insight into relevant sectors and geographies",
      "Focused company and market intelligence",
      "Evidence to support where commercial effort should be directed",
    ],
  },
  {
    id: "target-account-identification",
    num: "02",
    title: "Target Account Identification",
    short:
      "Build focused lists of organisations worth pursuing and prioritise them according to opportunity and strategic fit.",
    challenge:
      "Sales effort is often spread thinly across long, unqualified lists. Without a clear view of which organisations genuinely fit your offer, time is spent on accounts that were never likely to convert.",
    does: "We define the characteristics of your ideal target accounts, then identify and prioritise the organisations that match, scoring them against opportunity, timing and strategic fit.",
    receive: [
      "A prioritised target account list",
      "Account profiles covering activity, structure and relevance",
      "Clear prioritisation rationale for each account",
      "Recommendations for sequencing your outreach",
    ],
  },
  {
    id: "decision-maker-research",
    num: "03",
    title: "Decision-Maker Research",
    short:
      "Identify the people who influence buying decisions and understand where commercial conversations need to begin.",
    challenge:
      "In technical B2B markets, buying decisions involve multiple stakeholders. Approaching the wrong person, or the right person at the wrong level, can stall an opportunity before it begins.",
    does: "We map the people inside your target organisations who shape and influence buying decisions, clarifying roles, responsibilities and likely routes into the conversation.",
    receive: [
      "Key contact mapping across target accounts",
      "Role and influence context for each stakeholder",
      "Recommended entry points for commercial conversations",
      "Research summaries to support personalised outreach",
    ],
  },
  {
    id: "commercial-market-intelligence",
    num: "04",
    title: "Commercial Market Intelligence",
    short:
      "Understand how your market is moving: where investment is flowing, which organisations are active and where demand is forming.",
    challenge:
      "Technical markets shift constantly. Projects are sanctioned, paused and cancelled; organisations restructure; new entrants change the competitive picture. Decisions made on stale intelligence waste commercial effort.",
    does: "We research the commercial dynamics of your chosen markets, including project activity, investment signals, organisational change and supply chain movement, and translate them into practical insight.",
    receive: [
      "Opportunity intelligence on active and emerging demand",
      "Research summaries on market and project activity",
      "Competitive and supply chain context",
      "Clear implications for your business development focus",
    ],
  },
  {
    id: "crm-pipeline-enrichment",
    num: "05",
    title: "CRM & Pipeline Enrichment",
    short:
      "Turn an ageing contact database into a current, prioritised commercial asset.",
    challenge:
      "CRM data decays quickly. Contacts move on, companies change direction, and pipeline records quietly lose their value, undermining forecasting and focus.",
    does: "We review, verify and enrich your existing accounts and contacts, adding current intelligence on organisations, people and activity so your pipeline reflects the market as it is today.",
    receive: [
      "Verified and enriched account and contact records",
      "Pipeline enrichment with current market context",
      "Identification of gaps and stale records",
      "Recommendations for re-engagement priorities",
    ],
  },
  {
    id: "business-development-advisory",
    num: "06",
    title: "Business Development Advisory",
    short:
      "Improve targeting, positioning and commercial focus with practical senior-level business development support.",
    challenge:
      "Business development effort often lacks a clear strategy underneath it. Activity happens, but it is not always aimed at the markets, accounts and people most likely to deliver growth.",
    does: "We work alongside your team as a senior commercial sounding board, bringing structure to targeting, sharpening positioning and focusing effort where it has the greatest potential return.",
    receive: [
      "A clearer business development strategy and focus",
      "Recommendations for outreach and market approach",
      "Sharper positioning for priority segments",
      "Practical, senior-level support for your team",
    ],
  },
];

export const FRACTIONAL = {
  id: "fractional-business-development",
  num: "07",
  title: "Fractional Business Development",
  short: "Senior BD support on a flexible basis, giving you leadership-level commercial focus without the fixed overhead.",
  blocks: [
    {
      label: "What It Is",
      title: "Senior business development, on your terms",
      body: "An experienced business development lead working inside your business for the time you actually need. You get the judgement, structure and market knowledge of a senior hire, without the permanent headcount, recruitment lead time or fixed cost.",
    },
    {
      label: "What You Get",
      title: "Focus, pipeline and momentum",
      body: "A defined target market and priority account list. Structured outreach to the decision-makers who matter. A pipeline that is reviewed honestly and moved forward every week. Clear reporting so leadership always knows where growth is coming from.",
    },
    {
      label: "How We Work",
      title: "Embedded, accountable, practical",
      body: "We agree commercial goals up front, then work alongside your team as part of it, attending the meetings, owning the targeting and doing the outreach. Intelligence, strategy and execution stay joined up, and the engagement flexes as your needs change.",
    },
  ],
  fit: [
    {
      sign: "Your pipeline depends on one or two people",
      detail: "Business development lives in the founder's diary or a single relationship. When they are busy, the pipeline stalls.",
    },
    {
      sign: "You need senior BD now, not after a six-month hire",
      detail: "The growth window is open today. Recruiting, onboarding and proving a permanent hire would take longer than you have.",
    },
    {
      sign: "Growth targets exist, but nobody owns the market plan",
      detail: "There is a number to hit, but no defined target market, priority accounts or structured approach behind it.",
    },
  ],
  timeline: [
    {
      days: "Days 1–30",
      phase: "Focus",
      headline: "Agree the goals. Define the market.",
      outcomes: [
        "Commercial objectives and success measures agreed with leadership",
        "Addressable market mapped and segmented by fit and activity",
        "Priority account list built, with the rationale for every name",
      ],
    },
    {
      days: "Days 31–60",
      phase: "Pipeline",
      headline: "Reach the right people. Open conversations.",
      outcomes: [
        "Decision-makers identified across priority accounts",
        "Structured outreach under way, with messaging tuned per segment",
        "First qualified conversations and meetings in the diary",
      ],
    },
    {
      days: "Days 61–90",
      phase: "Momentum",
      headline: "Progress opportunities. Report with clarity.",
      outcomes: [
        "Live opportunities being worked and moved forward weekly",
        "Pipeline reviewed honestly: what is real, what is not, what is next",
        "Clear reporting and a plan for the following quarter",
      ],
    },
  ],
};

export const OUTCOMES = [
  { id: "market-visibility", icon: "Compass", title: "Sharper Market Visibility", body: "See where the strongest opportunities are and focus commercial effort on the markets, sectors and geographies with the greatest potential." },
  { id: "qualified-opportunities", icon: "Target", title: "More Qualified Opportunities", body: "Prioritise prospects and opportunities that genuinely fit your proposition, reducing wasted time on low-value or poorly matched targets." },
  { id: "commercial-pipelines", icon: "TrendingUp", title: "Stronger Commercial Pipelines", body: "Build a more consistent flow of relevant prospects, conversations and opportunities to support sustainable growth." },
  { id: "market-entry", icon: "Globe", title: "Faster Market Entry", body: "Enter new sectors, regions and countries with greater clarity around the market, key players and likely routes to opportunity." },
  { id: "decision-makers", icon: "Users", title: "Better Access to Decision-Makers", body: "Identify and engage the people who influence, specify and approve buying decisions within your target accounts." },
  { id: "focused-bd", icon: "Crosshair", title: "More Focused Business Development", body: "Give your team clearer priorities on where to spend time, who to target and which opportunities deserve attention." },
  { id: "commercial-confidence", icon: "ShieldCheck", title: "Greater Confidence in Commercial Decisions", body: "Make better growth decisions using clearer market intelligence and a stronger understanding of where to invest time and resource." },
];

export const PROCESS = [
  {
    num: "01",
    title: "Understand Your Offer",
    body: "We start by understanding what you sell, where you create value and what commercial growth looks like for your business.",
  },
  {
    num: "02",
    title: "Define Your Market",
    body: "We identify sectors, markets and buyer groups most relevant to your capabilities.",
  },
  {
    num: "03",
    title: "Map Target Companies",
    body: "We build a focused view of organisations that are most likely to require your services.",
  },
  {
    num: "04",
    title: "Identify Key People",
    body: "We identify decision-makers, influencers and relevant commercial contacts within those companies.",
  },
  {
    num: "05",
    title: "Support Action",
    body: "We turn the intelligence into practical recommendations that help your team approach the market with greater focus.",
  },
];

export const SECTORS = [
  {
    name: "Oil & Gas",
    blurb:
      "A mature but continually reshaping market where project sanctioning, late-life asset work and transition investment keep the supply chain in motion. We help you identify active operators and contractors, and the decision-makers behind upcoming work.",
  },
  {
    name: "Renewables",
    blurb:
      "A fast-moving landscape of developers, owners and investors across solar, onshore wind and emerging technologies. We map the organisations behind new projects and where commercial entry points are forming.",
  },
  {
    name: "Offshore Wind",
    blurb:
      "Long project cycles, consortia-based buying and complex supply chains make offshore wind difficult to navigate from the outside. We identify the developers, tier-one contractors and specialists worth targeting, and the people who shape procurement.",
  },
  {
    name: "Hydrogen",
    blurb:
      "An early-stage market where project pipelines shift quickly and partnerships define who buys from whom. We track where projects are genuinely progressing and which organisations are building commercial momentum.",
  },
  {
    name: "Power & Utilities",
    blurb:
      "Regulated, relationship-driven organisations with structured procurement and long planning horizons. We help you understand where investment programmes create demand and who controls the relevant budgets.",
  },
  {
    name: "Marine",
    blurb:
      "From vessel owners and operators to ports and marine services, buying decisions are global and relationship-led. We map the organisations and decision-makers most relevant to your offer.",
  },
  {
    name: "Chemicals",
    blurb:
      "A sector of large, complex organisations where demand is tied to plant investment, maintenance cycles and sustainability programmes. We identify where activity is planned and which stakeholders influence supplier selection.",
  },
  {
    name: "Technical & Engineering Services",
    blurb:
      "A broad market of consultancies, contractors and specialists serving energy and industrial clients. We help you understand the landscape, prioritise accounts and position your services where they fit best.",
  },
];

export const WHY_THRIVE = [
  {
    num: "01",
    title: "Commercial Thinking",
    body: "Built around real business development outcomes rather than theoretical strategy.",
  },
  {
    num: "02",
    title: "Market-Led Intelligence",
    body: "Understand where opportunity exists and where commercial attention should be focused.",
  },
  {
    num: "03",
    title: "Sharper Targeting",
    body: "Spend more time speaking to the businesses and people most relevant to your offer.",
  },
  {
    num: "04",
    title: "Practical Output",
    body: "Receive clear intelligence that can immediately support your sales and business development activity.",
  },
];

export const APPROACH = [
  {
    num: "01",
    title: "Focused",
    body: "We concentrate effort on the markets, accounts and people most likely to matter, and deliberately ignore the rest.",
  },
  {
    num: "02",
    title: "Commercial",
    body: "Every piece of work is judged against a simple standard: does it help our client win business?",
  },
  {
    num: "03",
    title: "Evidence-Led",
    body: "Recommendations are built on structured research and verifiable market signals, not assumption.",
  },
  {
    num: "04",
    title: "Practical",
    body: "Output is designed to be used immediately, by real teams, in real commercial conversations.",
  },
];
